import { validAggregate, type Aggregate } from '../../../supabase/functions/_shared/aggregateCore'
import { supabase } from '@/lib/supabase'

/**
 * Client wrapper for the wren-brain edge function.
 * All Wren AI text (insights, briefs, reasoning, chat, curriculum) goes through here.
 * Falls back gracefully when the edge function is unreachable or LLM keys are missing:
 * returns { text: null, sources: [] } and the caller renders its deterministic fallback.
 */

export type BrainTask = 'insight' | 'brief' | 'reasoning' | 'curriculum' | 'chat'

export interface BrainSource {
  slug: string
  title: string
  publisher: string | null
  url: string | null
  year: number | null
  snippet: string | null
}

export interface BrainCapInfo {
  reason: 'calls' | 'input_tokens'
  callsToday: number
  inputTokensToday: number
  capCalls: number
  capInputTokens: number
  retryAfterHours: number
}

export interface BrainResult {
  text: string | null
  sources: BrainSource[]
  tier: 't2' | 't3' | 't4' | null
  model: string | null
  snapshot: Record<string, unknown> | null
  aggregates?: Record<string, Aggregate>
  error?: string
  /**
   * True when wren-brain returned HTTP 429 for a per-family daily cap.
   * Callers should stop retrying and surface `capInfo` to the user until the reset window.
   */
  capExceeded?: boolean
  capInfo?: BrainCapInfo
}

export async function askBrain(params: {
  task: BrainTask
  personId: string
  baseTitle?: string
  baseBody?: string
  userMessage?: string
  responseMode?: 'evidence_only' | 'companion_greeting' | 'companion_sleep'
}): Promise<BrainResult> {
  try {
    const { data, error } = await supabase.functions.invoke('wren-brain', {
      body: {
        task: params.task,
        person_id: params.personId,
        base_title: params.baseTitle,
        base_body: params.baseBody,
        user_message: params.userMessage,
        response_mode: params.responseMode,
      },
    })
    if (error) {
      // supabase-js v2 sets `error` on non-2xx responses. When wren-brain returns HTTP 429 for a
      // per-family daily cap, the JSON body is still on the underlying Response — read it and
      // surface a structured cap error so the caller can stop retrying and message the user.
      const capInfo = await extractCapInfo(error)
      if (capInfo) {
        return {
          text: null,
          sources: [],
          tier: null,
          model: null,
          snapshot: null,
          error: 'daily_cap',
          capExceeded: true,
          capInfo,
        }
      }
      return { text: null, sources: [], tier: null, model: null, snapshot: null, error: error.message }
    }
    const d = data as {
      ok: boolean
      text: string | null
      sources: BrainSource[]
      tier: 't2' | 't3' | 't4' | null
      model: string | null
      snapshot: Record<string, unknown>
      llm_error?: string
    }
    if (!d.snapshot || !Object.values(d.snapshot).every(validAggregate)) throw new Error('Unverified aggregate response')
    return {
      aggregates: d.snapshot as Record<string, Aggregate>,
      text: d.text ?? null,
      sources: d.sources ?? [],
      tier: d.tier ?? null,
      model: d.model ?? null,
      snapshot: d.snapshot ?? null,
      error: d.llm_error,
    }
  } catch (e) {
    return {
      text: null,
      sources: [],
      tier: null,
      model: null,
      snapshot: null,
      error: e instanceof Error ? e.message : String(e),
    }
  }
}

interface FunctionsHttpErrorLike {
  context?: Response | { response?: Response }
}

async function extractCapInfo(err: unknown): Promise<BrainCapInfo | null> {
  const ctx = (err as FunctionsHttpErrorLike | null)?.context
  const resp: Response | undefined =
    ctx instanceof Response ? ctx :
    (ctx && typeof ctx === 'object' && 'response' in ctx && ctx.response instanceof Response) ? ctx.response :
    undefined
  if (!resp || resp.status !== 429) return null
  try {
    const body = await resp.clone().json() as {
      error?: string
      reason?: 'calls' | 'input_tokens'
      calls_today?: number
      input_tokens_today?: number
      cap_calls?: number
      cap_input_tokens?: number
      retry_after_hours?: number
    }
    if (body?.error !== 'daily_cap' || !body.reason) return null
    return {
      reason: body.reason,
      callsToday: body.calls_today ?? 0,
      inputTokensToday: body.input_tokens_today ?? 0,
      capCalls: body.cap_calls ?? 0,
      capInputTokens: body.cap_input_tokens ?? 0,
      retryAfterHours: body.retry_after_hours ?? 0,
    }
  } catch {
    return null
  }
}
